import { supabase } from "../../supabase/supabase";
import { ProviderCredential } from "@/types/providerCredentials";
import * as Sentry from "@sentry/react";

export const providerCredentialsService = {
  async getProviderCredentials(
    organizationId: string,
  ): Promise<ProviderCredential[]> {
    const { data, error } = await supabase
      .from("provider_credentials")
      .select("*")
      .eq("organization_id", organizationId);

    if (error) {
      Sentry.logger.error('Error fetching provider credentials', { 
        error,
        log_source: 'provider_credentials_service',
        organization_id: organizationId
      });
      throw error;
    }

    return data || [];
  },

  async getProviderCredential(id: string): Promise<ProviderCredential> {
    const { data, error } = await supabase
      .from("provider_credentials")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      Sentry.logger.error('Error fetching provider credential', { 
        error,
        log_source: 'provider_credentials_service',
        credential_id: id
      });
      throw error;
    }

    return data;
  },

  async createProviderCredential(
    credential: Omit<ProviderCredential, "id" | "created_at" | "updated_at">,
  ): Promise<ProviderCredential> {
    const { data, error } = await supabase
      .from("provider_credentials")
      .insert(credential)
      .select()
      .single();

    if (error) {
      Sentry.logger.error('Error creating provider credential', { 
        error,
        log_source: 'provider_credentials_service',
        provider: credential.provider_name
      });
      throw error;
    }

    return data;
  },

  async updateProviderCredential(
    id: string,
    updates: Partial<
      Omit<
        ProviderCredential,
        "id" | "created_at" | "updated_at" | "organization_id"
      >
    >,
  ): Promise<ProviderCredential> {
    const { data, error } = await supabase
      .from("provider_credentials")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      Sentry.logger.error('Error updating provider credential', { 
        error,
        log_source: 'provider_credentials_service',
        credential_id: id
      });
      throw error;
    }

    return data;
  },

  async deleteProviderCredential(id: string): Promise<void> {
    const { error } = await supabase
      .from("provider_credentials")
      .delete()
      .eq("id", id);

    if (error) {
      Sentry.logger.error('Error deleting provider credential', { 
        error,
        log_source: 'provider_credentials_service',
        credential_id: id
      });
      throw error;
    }
  },

  async toggleProviderCredentialStatus(
    id: string,
    isActive: boolean,
  ): Promise<ProviderCredential> {
    const { data, error } = await supabase
      .from("provider_credentials")
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      Sentry.logger.error('Error toggling provider credential status', { 
        error,
        log_source: 'provider_credentials_service',
        credential_id: id,
        is_active: isActive
      });
      throw error;
    }

    return data;
  },
};
